import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit3, Trash2, Eye, Save, X, Upload, Calendar, ExternalLink, FileText, Sparkles, Wand2, Wrench } from "lucide-react";
import BlogCoverImage from "@/components/BlogCoverImage";
import { safeHtml } from "@/lib/sanitize";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  published: "bg-green-100 text-green-700",
  draft: "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
};

const CATEGORIES = ["HR Strategy", "Hiring", "L&D", "Leadership", "AI in HR", "Performance", "Culture"];

function emptyDraft() {
  return {
    _id: null,
    title: "",
    slug: "",
    category: "HR Strategy",
    excerpt: "",
    meta_description: "",
    content: "",
    status: "draft",
    cover_image: "",
    tags: [],
    author: "RoleIQ Team",
    scheduled_publish_at: "",
  };
}

function renderMarkdownPreview(text) {
  // Lightweight: paragraphs split by blank lines, **bold**, *italic*, line breaks
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const blocks = (text || "").split(/\n\n+/);
  return blocks.map((block, i) => {
    let html = escape(block);
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
    html = html.replace(/\n/g, "<br/>");
    if (block.startsWith("## ")) {
      return <h2 key={i} className="font-playfair text-navy text-xl font-semibold mt-4 mb-2" dangerouslySetInnerHTML={{ __html: safeHtml(html.slice(3)) }} />;
    }
    return <p key={i} className="font-dm text-navy/80 text-sm leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />;
  });
}

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // draft object
  const [showEditor, setShowEditor] = useState(false);
  const [tab, setTab] = useState("all");
  const [genTopic, setGenTopic] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/blogs`, { withCredentials: true });
      setPosts(r.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(emptyDraft()); setShowEditor(true); };
  const startEdit = (p) => { setEditing({ ...emptyDraft(), ...p, tags: p.tags || [] }); setShowEditor(true); };
  const closeEditor = () => { setShowEditor(false); setEditing(null); };

  const save = async (publishNow = false) => {
    if (!editing) return;
    const payload = { ...editing };
    if (publishNow) payload.status = "published";
    if (!payload.title?.trim() || !payload.content?.trim()) { alert("Title and content are required"); return; }
    try {
      if (editing._id) {
        await axios.patch(`${API}/api/admin/blogs/${editing._id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/blogs`, payload, { withCredentials: true });
      }
      closeEditor();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Save failed";
      if (err?.response?.status === 401) {
        alert("Your session has expired. Please log in again — your draft is still in this form, so re-login in another tab, come back, and click Publish.");
      } else {
        alert(detail);
      }
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/api/admin/blogs/${id}`, { withCredentials: true });
      load();
    } catch (err) { alert("Delete failed"); }
  };

  const generateAI = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/api/admin/blogs/generate`, { topic: genTopic || null, status: "draft", with_cover: true }, { withCredentials: true });
      setGenTopic("");
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const regenerateCover = async (id) => {
    if (!window.confirm("Regenerate cover using Nano Banana? This replaces the existing cover.")) return;
    try {
      await axios.post(`${API}/api/admin/blogs/${id}/regenerate-cover`, {}, { withCredentials: true });
      load();
    } catch (err) { alert(err?.response?.data?.detail || "Regenerate failed"); }
  };

  const [repairing, setRepairing] = useState(false);
  const repairMissingCovers = async () => {
    if (!window.confirm("Scan all blogs and regenerate covers whose underlying file is missing on disk?\n\nUse this after a production redeploy that wiped the uploads folder. Cap: 20 covers per run.")) return;
    setRepairing(true);
    try {
      const r = await axios.post(`${API}/api/admin/blogs/repair-covers`, {}, { withCredentials: true });
      const { scanned, repaired_count, failed_count, needs_manual_reupload_count, repaired, failed, needs_manual_reupload } = r.data;
      const summary = `Scanned: ${scanned}\nRepaired (AI covers): ${repaired_count}\nNeeds manual re-upload (your custom covers): ${needs_manual_reupload_count || 0}\nFailed: ${failed_count}` +
        (repaired_count ? `\n\nRepaired:\n${repaired.map(x => "• " + x.title).join("\n")}` : "") +
        ((needs_manual_reupload_count || 0) ? `\n\nNeeds manual re-upload (open the editor and pick a new file):\n${(needs_manual_reupload || []).map(x => "• " + x.title).join("\n")}` : "") +
        (failed_count ? `\n\nFailed:\n${failed.map(x => "• " + x.title + " — " + x.reason).join("\n")}` : "");
      alert(summary);
      load();
    } catch (err) { alert(err?.response?.data?.detail || "Repair failed"); }
    finally { setRepairing(false); }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/api/admin/blogs/upload-cover`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setEditing(prev => ({ ...prev, cover_image: r.data.url }));
    } catch (err) { alert(err?.response?.data?.detail || "Upload failed"); }
  };

  const filtered = tab === "all" ? posts : posts.filter(p => p.status === tab);

  return (
    <div className="space-y-6" data-testid="blog-manager">
      {/* Header + Actions */}
      <div className="bg-white rounded-xl shadow-md p-5 flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h2 className="font-playfair text-navy text-lg font-bold">Admin Blog Manager</h2>
          <p className="font-dm text-navy/60 text-xs">Write, schedule, and publish marketing articles. Featured community insights surface alongside on /blog.</p>
        </div>
        <button onClick={startNew} data-testid="new-blog-btn" className="bg-navy text-gold font-dm font-bold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-navy/90">
          <Plus className="w-4 h-4" /> New Article
        </button>
        <button onClick={repairMissingCovers} disabled={repairing} data-testid="repair-covers-btn" title="Regenerate covers whose files are missing on disk (use after a redeploy)"
          className="bg-amber-100 text-amber-800 border border-amber-300 font-dm font-bold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-amber-200 disabled:opacity-60">
          <Wrench className="w-4 h-4" /> {repairing ? "Repairing…" : "Repair missing covers"}
        </button>
      </div>

      {/* AI Generate */}
      <div className="bg-gradient-to-r from-gold/10 to-navy/5 border border-gold/20 rounded-xl p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <Sparkles className="w-5 h-5 text-gold mt-1" />
          <div className="flex-1 min-w-[250px]">
            <h3 className="font-dm text-navy font-bold text-sm">Generate with Claude AI</h3>
            <p className="font-dm text-navy/60 text-xs mb-3">Drafts an 800-1100 word article. Saves as Draft so you can review before publishing.</p>
            <div className="flex flex-wrap gap-2">
              <input value={genTopic} onChange={e => setGenTopic(e.target.value)} placeholder="Optional topic e.g. 'Remote-first leadership skills'"
                data-testid="ai-topic-input"
                className="flex-1 min-w-[200px] border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
              <button onClick={generateAI} disabled={generating} data-testid="ai-generate-btn"
                className="bg-gold text-navy font-dm font-bold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50">
                {generating ? "Generating..." : <><Sparkles className="w-4 h-4" /> Generate Draft</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-lg p-1 w-fit shadow-sm flex-wrap">
        {[
          { id: "all", name: `All (${posts.length})` },
          { id: "draft", name: `Drafts (${posts.filter(p => p.status === "draft").length})` },
          { id: "scheduled", name: `Scheduled (${posts.filter(p => p.status === "scheduled").length})` },
          { id: "published", name: `Published (${posts.filter(p => p.status === "published").length})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`blog-tab-${t.id}`}
            className={`font-dm text-sm px-4 py-2 rounded-lg transition-colors ${tab === t.id ? "bg-navy text-white" : "text-navy/50 hover:text-navy"}`}>
            {t.name}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <p className="font-dm text-navy/40 text-sm text-center py-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="font-dm text-navy/40 text-sm text-center py-10">No articles in this view.</p>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-navy/5 border-b border-navy/10">
                <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Title</th>
                <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Category</th>
                <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Status</th>
                <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Source</th>
                <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Updated</th>
                <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p._id} className="border-b border-navy/5 hover:bg-cream/50" data-testid={`blog-row-${p.slug}`}>
                  <td className="py-3 px-4">
                    <p className="font-dm text-navy text-sm font-semibold line-clamp-1">{p.title}</p>
                    <p className="font-dm text-navy/40 text-[10px]">/blog/{p.slug}</p>
                  </td>
                  <td className="font-dm text-navy/70 text-xs py-3 px-4">{p.category}</td>
                  <td className="py-3 px-4">
                    <span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[p.status] || "bg-gray-100"}`}>{p.status}</span>
                    {p.scheduled_publish_at && p.status !== "published" && (
                      <p className="font-dm text-navy/40 text-[10px] mt-1 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {new Date(p.scheduled_publish_at).toLocaleDateString("en-IN")}</p>
                    )}
                  </td>
                  <td className="font-dm text-navy/60 text-xs py-3 px-4">{p.source || "manual"}</td>
                  <td className="font-dm text-navy/50 text-[10px] py-3 px-4">{p.updated_at ? new Date(p.updated_at).toLocaleDateString("en-IN") : "-"}</td>
                  <td className="py-3 px-4 space-x-2 whitespace-nowrap">
                    <button onClick={() => startEdit(p)} data-testid={`edit-blog-${p.slug}`} className="text-gold hover:text-gold/80"><Edit3 className="w-4 h-4 inline" /></button>
                    <button onClick={() => regenerateCover(p._id)} data-testid={`regen-cover-${p.slug}`} title="Regenerate cover via Nano Banana" className="text-navy/60 hover:text-navy"><Wand2 className="w-4 h-4 inline" /></button>
                    {p.status === "published" && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="text-navy/60 hover:text-navy inline-block"><ExternalLink className="w-4 h-4 inline" /></a>
                    )}
                    <button onClick={() => del(p._id)} data-testid={`delete-blog-${p.slug}`} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && editing && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8" data-testid="blog-editor-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white rounded-t-2xl z-10">
              <h3 className="font-playfair text-navy text-lg font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" /> {editing._id ? "Edit Article" : "New Article"}
              </h3>
              <button onClick={closeEditor} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 p-6">
              {/* Left: form fields */}
              <div className="space-y-3">
                <div>
                  <label className="font-dm text-navy/70 text-xs font-bold">Title</label>
                  <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="editor-title"
                    className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                </div>
                <div>
                  <label className="font-dm text-navy/70 text-xs font-bold">SEO Slug</label>
                  <input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} placeholder="auto-generated from title if empty"
                    data-testid="editor-slug"
                    className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm font-mono" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-dm text-navy/70 text-xs font-bold">Category</label>
                    <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })}
                      className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="font-dm text-navy/70 text-xs font-bold">Author</label>
                    <input value={editing.author || ""} onChange={e => setEditing({ ...editing, author: e.target.value })}
                      className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                  </div>
                </div>
                <div>
                  <label className="font-dm text-navy/70 text-xs font-bold">Meta Description (160 chars max)</label>
                  <textarea value={editing.meta_description || ""} onChange={e => setEditing({ ...editing, meta_description: e.target.value.slice(0, 160) })} rows={2}
                    data-testid="editor-meta-desc"
                    className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                  <p className="font-dm text-navy/40 text-[10px] text-right">{(editing.meta_description || "").length}/160</p>
                </div>
                <div>
                  <label className="font-dm text-navy/70 text-xs font-bold">Excerpt</label>
                  <textarea value={editing.excerpt || ""} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} rows={2}
                    className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                </div>
                <div>
                  <label className="font-dm text-navy/70 text-xs font-bold">Cover Image</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="file" accept="image/*" onChange={e => uploadCover(e.target.files?.[0])} data-testid="editor-cover-upload"
                      className="flex-1 font-dm text-xs file:bg-navy file:text-gold file:font-bold file:border-0 file:px-3 file:py-1.5 file:rounded-lg" />
                    {editing.cover_image && (
                      <BlogCoverImage src={editing.cover_image} alt="cover" className="w-16 h-12 object-cover rounded border border-navy/10" placeholderClassName="rounded border border-navy/10" />
                    )}
                  </div>
                  {editing.cover_image && <button onClick={() => setEditing({ ...editing, cover_image: "" })} className="font-dm text-red-500 text-[10px] hover:underline mt-1">Remove cover</button>}
                </div>
                <div>
                  <label className="font-dm text-navy/70 text-xs font-bold">Tags (comma separated)</label>
                  <input value={(editing.tags || []).join(", ")} onChange={e => setEditing({ ...editing, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })}
                    className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-dm text-navy/70 text-xs font-bold">Status</label>
                    <select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })} data-testid="editor-status"
                      className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-dm text-navy/70 text-xs font-bold flex items-center gap-1"><Calendar className="w-3 h-3" /> Publish At</label>
                    <input type="datetime-local" value={(editing.scheduled_publish_at || "").slice(0, 16)} onChange={e => setEditing({ ...editing, scheduled_publish_at: e.target.value || null })}
                      data-testid="editor-schedule"
                      className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                  </div>
                </div>
              </div>

              {/* Right: content editor + preview */}
              <div className="space-y-3">
                <label className="font-dm text-navy/70 text-xs font-bold flex items-center justify-between">
                  Content (Markdown supported: **bold**, *italic*, ## heading, blank line = paragraph)
                  <span className="text-navy/40 font-normal">{(editing.content || "").split(/\s+/).filter(Boolean).length} words</span>
                </label>
                <textarea value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} rows={12}
                  data-testid="editor-content"
                  className="w-full border border-navy/20 rounded-lg px-3 py-2 font-mono text-xs leading-relaxed" />

                <div>
                  <p className="font-dm text-navy/70 text-xs font-bold mb-2 flex items-center gap-1"><Eye className="w-3 h-3" /> Live Preview</p>
                  <div className="bg-cream/50 rounded-lg p-4 max-h-72 overflow-y-auto" data-testid="editor-preview">
                    {renderMarkdownPreview(editing.content)}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-navy/10 px-6 py-4 flex flex-wrap items-center justify-end gap-3 bg-cream/30 rounded-b-2xl">
              <button onClick={closeEditor} className="font-dm text-navy/60 text-sm px-4 py-2 hover:text-navy">Cancel</button>
              <button onClick={() => save(false)} data-testid="editor-save-draft"
                className="bg-white border border-navy/20 text-navy font-dm font-bold px-4 py-2 rounded-lg hover:bg-navy/5 flex items-center gap-2">
                <Save className="w-4 h-4" /> Save
              </button>
              <button onClick={() => save(true)} data-testid="editor-publish"
                className="bg-gold text-navy font-dm font-bold px-4 py-2 rounded-lg hover:bg-gold/90 flex items-center gap-2">
                <Upload className="w-4 h-4" /> Publish Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
